import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showToast } from '@quickmd-llc/design-system'
import {
  getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey,
  postPatientsPatientIdPharmacyOrdersOrderIdFulfillment,
} from 'api/generated/pharmacy-fulfillment/pharmacy-fulfillment'
import { getGetPharmacyOrdersQueryKey } from 'api/generated/pharmacy-order/pharmacy-order'
import { clearFailedFulfillmentAttempt, restoreFailedFulfillmentAttempt } from './fulfillmentAttempts'
import { getFulfillmentErrorMessage } from './fulfillmentErrors'
import { isScriptlyOrderPending, markScriptlyOrderPending, releaseScriptlyOrderPending } from './pendingScriptlyOrders'

type ScriptlyOrderIds = { patientId: string; orderId: string }

export function useCreateScriptlyOrder() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ patientId, orderId }: ScriptlyOrderIds) =>
      postPatientsPatientIdPharmacyOrdersOrderIdFulfillment(patientId, orderId),
    // Marked before the request: the 200 returns once queued, and the MQTT outcome can beat it back.
    onMutate: ({ orderId }) => {
      // The pharmacist is acting on the last final failure, so its message no longer applies. It is
      // cleared now rather than on success because the MQTT outcome can beat the 200 back, and a late
      // clear would wipe it; the request failing puts it back (onError).
      const clearedFailure = clearFailedFulfillmentAttempt(queryClient, orderId)
      markScriptlyOrderPending(orderId)
      return { clearedFailure }
    },
    onSuccess: (_data, { orderId }) => {
      // An MQTT outcome that beat the 200 back already consumed the mark and toasted the real result;
      // a "Sent to Scriptly" toast now would replace it (the app shows one toast at a time).
      if (!isScriptlyOrderPending(orderId)) return
      showToast({ type: 'success', message: 'Sent to Scriptly. This order updates when the pharmacy responds.' })
    },
    onError: (error: unknown, { orderId }, context) => {
      // Nothing was queued, so the order is still in the state that message described.
      if (context?.clearedFailure) restoreFailedFulfillmentAttempt(queryClient, orderId, context.clearedFailure)
      // Release only this attempt's mark: an earlier accepted trigger may still be awaiting its outcome.
      releaseScriptlyOrderPending(orderId)
      showToast({ type: 'error', message: getFulfillmentErrorMessage(error, 'Could not send the order to Scriptly.') })
    },
    // Either way eligibility has changed (sent < 15 min, or the 4xx says it was never eligible).
    onSettled: (_data, _error, { patientId, orderId }) => {
      void queryClient.invalidateQueries({ queryKey: getGetPharmacyOrdersQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey(patientId, orderId),
      })
    },
  })

  return {
    createScriptlyOrder: (ids: ScriptlyOrderIds) => mutation.mutate(ids),
    isCreatingScriptlyOrder: mutation.isPending,
  }
}
